// lcdcash-frontend/src/components/services/Chiffre.jsx
import * as React from 'react';
import { Container, Box, Typography, Paper, Stack, Chip, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import chiffreI18n from '../../i18n/components/services/chiffre.i18n';

const GREEN = '#0466c8';
const GREEN_BG = 'rgba(67, 72, 160, 0.08)';

export default function Chiffre() {
  const { t, register } = useI18n();
  React.useEffect(() => { register('services', chiffreI18n); }, [register]);

  return (
    <Box sx={{ bgcolor: '#fff', borderTop: '1px solid #eee' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 7 }, pb: { xs: 3, md: 4.5 } }}>
        {/* Titre section */}
        <Typography
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: 22, sm: 24, md: 28 },
            lineHeight: 1.2,
            mb: { xs: 2, md: 2.5 },
          }}
        >
          {t('services.chiffre.title')}
        </Typography>

        {/* Cartes KPI */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          {/* Transactions */}
          <KpiCard
            icon={<ReceiptLongOutlinedIcon />}
            title="+300"
            subtitle={t('services.chiffre.cards.transactions.subtitle')}
            pill={t('services.chiffre.cards.transactions.pill')}
          />

          {/* Satisfaction 4,5/5 */}
          <KpiRating
            rating={4.5}
            subtitle={t('services.chiffre.cards.rating.subtitle')}
            pill={t('services.chiffre.cards.rating.pill')}
            tooltip={t('services.chiffre.tooltip.illustrative')}
          />

          {/* Paiement moyen */}
          <KpiCard
            icon={<PaidOutlinedIcon />}
            title="< 48h"
            subtitle={t('services.chiffre.cards.payment.subtitle')}
            pill={t('services.chiffre.cards.payment.pill')}
          />

          {/* Taux de recommandation */}
          <KpiCard
            icon={<ThumbUpAltOutlinedIcon />}
            title="95%"
            subtitle={t('services.chiffre.cards.recommendation.subtitle')}
            pill={t('services.chiffre.cards.recommendation.pill')}
          />
        </Box>

        {/* Bandeau de confiance (optionnel) – laissé en commentaire, inchangé */}
        {/*
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 3, md: 4 },
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            border: '1px solid #e9f2ea',
            background: GREEN_BG,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpIcon sx={{ color: GREEN }} />
              <Typography sx={{ fontWeight: 800, color: GREEN }}>
                Transparence & rapidité garanties
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label="Estimation claire" sx={{ bgcolor: '#fff', fontWeight: 700 }} />
              <Chip size="small" label="Contrôle expert" sx={{ bgcolor: '#fff', fontWeight: 700 }} />
              <Chip size="small" label="Paiement immédiat" sx={{ bgcolor: '#fff', fontWeight: 700 }} />
            </Stack>
          </Stack>
        </Paper>
        */}
      </Container>
    </Box>
  );
}

/* ===== Sous-composants ===== */

function KpiCard({ icon, title, subtitle, pill }) {
  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          bgcolor: GREEN_BG,
          color: GREEN,
          borderRadius: 2,
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { fontSize: 'small' })}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: 22, md: 24 },
            lineHeight: 1.1,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {subtitle}
        </Typography>
        {pill && (
          <Chip
            label={pill}
            size="small"
            sx={{ mt: 1, bgcolor: GREEN_BG, color: GREEN, fontWeight: 700 }}
          />
        )}
      </Box>
    </Paper>
  );
}

function KpiRating({ rating = 4.5, subtitle, pill, tooltip }) {
  // étoiles pleines / demi / vides (visuel simple)
  const full = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.25 && rating % 1 < 0.75;

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          bgcolor: GREEN_BG,
          color: GREEN,
          borderRadius: 2,
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <StarIcon fontSize="small" />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.25} alignItems="center">
          {Array.from({ length: full }).map((_, i) => (
            <StarIcon key={`s-${i}`} sx={{ color: '#FFC107' }} fontSize="small" />
          ))}
          {hasHalf && <StarHalfIcon sx={{ color: '#FFC107' }} fontSize="small" />}
          {Array.from({ length: 5 - full - (hasHalf ? 1 : 0) }).map((_, i) => (
            <StarIcon key={`e-${i}`} sx={{ color: 'rgba(0,0,0,0.2)' }} fontSize="small" />
          ))}
          <Typography sx={{ ml: 0.5, fontWeight: 800 }}>
            {rating.toString().replace('.', ',')}/5
          </Typography>
        </Stack>

        <Typography sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {subtitle}
        </Typography>
        {pill && (
          <Tooltip title={tooltip || ''}>
            <Chip
              label={pill}
              size="small"
              sx={{ mt: 1, bgcolor: GREEN_BG, color: GREEN, fontWeight: 700 }}
            />
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}